import { Flex, Text, Skeleton } from '@chakra-ui/react';
import React from 'react';
import { Navigate } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';

import {
  useGetOrganizationLoanProductCreditFacilityGroupingsForAssignmentQuery,
  useGetAssignmentStrategiesForOrganizationQuery,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { AssignmentStrategiesForImplementation } from 'packages/loanProduct/ui/assignmentStrategy/AssignmentStrategiesForImplementation';
import { CreateAssignmentStrategyButton } from 'packages/loanProduct/ui/assignmentStrategy/CreateAssignmentStrategyButton';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

// Fragments
const RouteBase = ({ organizationId }: { organizationId: string }) => {
  const {
    data: groupings,
    loading: groupingsLoading,
  } = useGetOrganizationLoanProductCreditFacilityGroupingsForAssignmentQuery({
    variables: { organizationId },
  });

  const {
    data: assignmentStrategies,
    loading: assignmentStrategiesLoading,
    refetch: assignmentStrategiesRefetch,
  } = useGetAssignmentStrategiesForOrganizationQuery({
    variables: { organizationId },
    fetchPolicy: 'network-only',
  });

  if (
    groupingsLoading ||
    !groupings ||
    assignmentStrategiesLoading ||
    !assignmentStrategies
  ) {
    return <Skeleton minHeight="50vh" />;
  }

  return (
    <>
      <Text fontSize="lg" mb="32px" color="gray">
        Select the strategies to set as defaults for future proposals, create
        new strategies, or modify existing ones.
      </Text>
      <Flex justifyContent="flex-start" mb="32px">
        <CreateAssignmentStrategyButton
          loanProductCreditFacilityGroupings={
            groupings.organization.loanProductCreditFacilityGroupsForAssignment
          }
        />
      </Flex>
      {groupings.organization.loanProductCreditFacilityGroupsForAssignment.map(
        (grouping) => {
          const implementationStrategiesData = assignmentStrategies.assignmentStrategiesForOrganization.find(
            (implStrategies) =>
              implStrategies.implementationId === grouping.implementationId,
          );
          finleyAssert(
            implementationStrategiesData,
            `Unable to find assignment strategies for implementation ${grouping.implementationId}`,
          );
          return (
            <AssignmentStrategiesForImplementation
              key={grouping.implementationId}
              lpToCfEntityGrouping={grouping}
              assignmentStrategies={
                implementationStrategiesData.assignmentStrategies
              }
              refetchStrategies={() =>
                assignmentStrategiesRefetch({ organizationId })
              }
            />
          );
        },
      )}
    </>
  );
};

// Exports
export const Route = () => {
  const { user } = useUserAuthOrThrow();

  // Visibility rules
  if (user.userType !== 'BORROWER') return <Navigate to="/settings" />;

  return (
    <RouteErrorBoundary
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <Navigate to="/settings" />;
      }}
    >
      <RouteBase organizationId={user.organizationId} />
    </RouteErrorBoundary>
  );
};
